import { Col, message, Spin } from "antd";
import Text from "antd/lib/typography/Text";
import Title from "antd/lib/typography/Title";
import { SearchOutlined } from "@ant-design/icons";
import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BUTTON_SIZE, INPUT_SIZE } from "../../../common/ThemeConstants";
import FormButton from "../../../components/general/FormButton";
import FormContainer from "../../../components/general/FormContainer";
import FormSelect from "../../../components/general/FormSelect";
import FormTextField from "../../../components/general/FormTextField";
import ReportExcelDownload from "../../../components/ReportingComponents/ReportExcelDownload";
import { getDate } from "../../../functions/dateFunctions";
import { setBranches } from "../../../redux/actions/reportActions";
import { postRequest } from "../../../services/mainApp.service";
import CC_SalesSummaryTemp from "./CC_SalesSummaryTemp";

const pad2 = (n) => String(n).padStart(2, "0");

/** Build `YYYY-MM-DDTHH:mm` for datetime-local from a business date string. */
function businessDateToDateTimeLocalStart(dateStr) {
  const s = String(dateStr ?? "").trim();
  const parts = s.split(/[-T]/).slice(0, 3).map((x) => parseInt(x, 10));
  if (parts.length < 3 || parts.some((n) => Number.isNaN(n))) {
    const d = new Date();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T00:00`;
  }
  const [y, m, d] = parts;
  return `${y}-${pad2(m)}-${pad2(d)}T00:00`;
}

function businessDateToDateTimeLocalEnd(dateStr) {
  const s = String(dateStr ?? "").trim();
  const parts = s.split(/[-T]/).slice(0, 3).map((x) => parseInt(x, 10));
  if (parts.length < 3 || parts.some((n) => Number.isNaN(n))) {
    const d = new Date();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T23:59`;
  }
  const [y, m, d] = parts;
  return `${y}-${pad2(m)}-${pad2(d)}T23:59`;
}

/** Match existing getDate() style (no leading zeros on month/day). */
function dateTimeLocalToBusinessDate(dtLocal) {
  const datePart = String(dtLocal ?? "").split("T")[0];
  const segs = datePart.split("-").map((x) => parseInt(x, 10));
  if (segs.length < 3 || segs.some((n) => Number.isNaN(n))) return getDate();
  const [y, m, d] = segs;
  return `${y}-${m}-${d}`;
}

function formatDateTimeForApi(dtLocal) {
  const raw = String(dtLocal ?? "").trim();
  if (!raw.includes("T")) return raw;
  const [date, time] = raw.split("T");
  const timePart =
    time && time.split(":").length === 2 ? `${time}:00` : time || "00:00:00";
  return `${date} ${timePart}`;
}

const emptyReport = {
  salesSummary: [],
  byPaymentTerms: [],
  byAgent: [],
  byArea: [],
  byOrderMode: [],
};

const CC_SalesSummary = () => {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.authReducer);
  const { branches } = useSelector((state) => state.reportsReducer);

  const [data, setData] = useState({
    BranchId: null,
    DateFrom: getDate(),
    DateTo: getDate(),
    FetchReportBy: "BusinessDate",
  });
  const [report, setReport] = useState(emptyReport);
  const [disableExcel, setDisableExcel] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    dispatch(
      setBranches(
        "/PosReports",
        {
          OperationId: 0,
          CompanyId: userData.CompanyId,
          BranchId: null,
        },
        controller,
        userData
      )
    );
    return () => controller.abort();
  }, [dispatch, userData.CompanyId, userData.UserId]);

  const handleSearchChange = (e) => {
    setData((prev) => ({ ...prev, [e.name]: e.value }));
    setReport(emptyReport);
    setDisableExcel(true);
  };

  const handleFetchReportByChange = (e) => {
    const next = e.target.value;
    setData((prev) => {
      if (next === prev.FetchReportBy) return prev;
      if (next === "OrderDate") {
        return {
          ...prev,
          FetchReportBy: next,
          DateFrom: businessDateToDateTimeLocalStart(prev.DateFrom),
          DateTo: businessDateToDateTimeLocalEnd(prev.DateTo),
        };
      }
      return {
        ...prev,
        FetchReportBy: next,
        DateFrom: dateTimeLocalToBusinessDate(prev.DateFrom),
        DateTo: dateTimeLocalToBusinessDate(prev.DateTo),
      };
    });
    setReport(emptyReport);
    setDisableExcel(true);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const dateFromVal =
      data.FetchReportBy === "OrderDate"
        ? formatDateTimeForApi(data.DateFrom)
        : data.DateFrom || getDate();
    const dateToVal =
      data.FetchReportBy === "OrderDate"
        ? formatDateTimeForApi(data.DateTo)
        : data.DateTo || getDate();

    if (dateFromVal && dateToVal) {
      const fromT = new Date(data.DateFrom);
      const toT = new Date(data.DateTo);
      if (fromT > toT) {
        message.error("Date From must be less than or equal to Date To");
        return;
      }
    }
    setLoading(true);
    const reportBy =
      data.FetchReportBy === "OrderDate" ? "OrderDate" : "BusinessDay";
    postRequest("/CallCenterSaleSummary", {
      OperationId: 1,
      CompanyId: userData.CompanyId,
      BranchId: data.BranchId,
      DateFrom: dateFromVal,
      DateTo: dateToVal,
      ReportBy: reportBy,
    })
      .then((res) => {
        setLoading(false);
        if (res.error) {
          message.error(res.errorMessage || "Failed to fetch report");
          setReport(emptyReport);
          setDisableExcel(true);
          return;
        }
        const ds = res?.data?.DataSet || {};
        const next = {
          salesSummary: ds.Table || [],
          byPaymentTerms: ds.Table1 || [],
          byOrderMode: ds.Table2 || [],
          byAgent: ds.Table3 || [],
          byArea: ds.Table4 || [],

        };
        const hasRows =
          next.salesSummary.length > 0 ||
          next.byPaymentTerms.length > 0 ||
          next.byAgent.length > 0 ||
          next.byArea.length > 0 ||
          next.byOrderMode.length > 0;
        if (!hasRows) {
          message.error("No records found!");
          setReport(emptyReport);
          setDisableExcel(true);
          return;
        }
        setReport(next);
        setDisableExcel(false);
      })
      .catch((err) => {
        setLoading(false);
        if (err.message !== "canceled") {
          console.error(err);
          message.error("Failed to fetch report");
        }
        setReport(emptyReport);
        setDisableExcel(true);
      });
  };

  const branchLabel =
    branches?.find((x) => x.BranchId === data.BranchId)?.BranchName || "";

  const searchPanel = (
    <Fragment>
      <FormSelect
        colSpan={6}
        listItem={branches || []}
        idName="BranchId"
        valueName="BranchName"
        size={INPUT_SIZE}
        name="BranchId"
        label="Branch"
        value={data.BranchId}
        onChange={handleSearchChange}
        allowClear={true}

      />
      <Col span={6} className="selectInput rounded-md">
        <Text>Fetch Report By</Text>
        <select
          className="form-select border-gray-300 rounded-md focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-2 h-10 mt-1 block w-full"
          name="FetchReportBy"
          value={data.FetchReportBy}
          onChange={handleFetchReportByChange}
          style={{ width: "100%" }}
        >
          <option value="BusinessDate">BusinessDate</option>
          <option value="OrderDate">OrderDate</option>
        </select>
      </Col>
      <FormTextField
        colSpan={6}
        label="Date From"
        type={data.FetchReportBy === "OrderDate" ? "datetime-local" : "date"}
        name="DateFrom"
        size={INPUT_SIZE}
        value={data.DateFrom}
        onChange={handleSearchChange}
      />
      <FormTextField
        colSpan={6}
        label="Date To"
        type={data.FetchReportBy === "OrderDate" ? "datetime-local" : "date"}
        name="DateTo"
        size={INPUT_SIZE}
        value={data.DateTo}
        onChange={handleSearchChange}
      />
      <FormButton
        title="Search"
        type="primary"
        size={BUTTON_SIZE}
        colSpan={2}
        htmlType="submit"
        icon={<SearchOutlined />}
      />
    </Fragment>
  );

  const showReport = !disableExcel;

  return (
    <div style={{ margin: 5 }}>
      <Title level={3} style={{ color: "#017971" }}>
        Call Center Sales Summary
      </Title>
      <div
        style={{
          background: "white",
          padding: 15,
          boxShadow: "0 0 5px lightgray",
          borderRadius: 2,
        }}
      >
        <FormContainer
          rowStyle={{ alignItems: "flex-end" }}
          onSubmit={handleSearchSubmit}
        >
          {searchPanel}
        </FormContainer>
        <div
          style={{ margin: "10px 0", borderTop: "1px solid lightgray" }}
        ></div>
      </div>

      <ReportExcelDownload
        fileName={`CC_SalesSummary_${data.DateFrom}_${data.DateTo}`}
      >
        {loading && (
          <Spin
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          />
        )}
        {showReport && (
          <CC_SalesSummaryTemp
            branch={branchLabel}
            fetchReportBy={data.FetchReportBy}
            dateFrom={data.DateFrom}
            dateTo={data.DateTo}
            salesSummary={report.salesSummary}
            byPaymentTerms={report.byPaymentTerms}
            byAgent={report.byAgent}
            byArea={report.byArea}
            byOrderMode={report.byOrderMode}
          />
        )}
      </ReportExcelDownload>
    </div>
  );
};

export default CC_SalesSummary;
